import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import Topbar from '../../components/common/Topbar';
import Toast from '../../components/common/Toast';
import { showToast } from '../../store/uiSlice';
import api from '../../services/api';

const ChangePassword = () => {
  const dispatch = useDispatch();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);

  const checkPasswordStrength = (pass) => {
    let strength = 0;
    if (pass.length >= 8) strength++;
    if (/[A-Z]/.test(pass) && /[a-z]/.test(pass)) strength++;
    if (/[0-9]/.test(pass)) strength++;
    if (/[^a-zA-Z0-9]/.test(pass)) strength++;
    setPasswordStrength(strength);
    return strength;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!currentPassword || !newPassword || !confirmPassword) { dispatch(showToast({ message: 'Please fill in all fields', type: 'error' })); return; }
    if (newPassword.length < 8) { dispatch(showToast({ message: 'New password must be at least 8 characters', type: 'error' })); return; }
    if (!/[A-Z]/.test(newPassword)) { dispatch(showToast({ message: 'Password needs at least one uppercase letter', type: 'error' })); return; }
    if (!/[a-z]/.test(newPassword)) { dispatch(showToast({ message: 'Password needs at least one lowercase letter', type: 'error' })); return; }
    if (!/[0-9]/.test(newPassword)) { dispatch(showToast({ message: 'Password needs at least one number', type: 'error' })); return; }
    if (!/[^a-zA-Z0-9]/.test(newPassword)) { dispatch(showToast({ message: 'Password needs at least one special character', type: 'error' })); return; }
    if (newPassword !== confirmPassword) { dispatch(showToast({ message: 'New passwords do not match', type: 'error' })); return; }
    setIsLoading(true);
    try {
      await api.put('/user/change-password', { currentPassword, newPassword });
      dispatch(showToast({ message: 'Password changed successfully!', type: 'success' }));
      setCurrentPassword(''); setNewPassword(''); setConfirmPassword('');
    } catch (error) {
      dispatch(showToast({ message: error.response?.data?.message || 'Failed to change password', type: 'error' }));
    } finally { setIsLoading(false); }
  };

  const strengthLabels = ['Too weak', 'Fair', 'Good', 'Strong'];
  const strengthColors = ['var(--color-red)', '#f59e0b', 'var(--color-text)', 'var(--color-green)'];

  const inputStyle = {
    background: 'var(--color-bgInput)', border: '1px solid var(--color-borderMedium)',
    color: 'var(--color-text)', outline: 'none',
  };

  const cardStyle = { background: 'var(--color-bgSurface)', border: '1px solid var(--color-border)' };

  return (
    <div className="min-h-screen" style={{ background: 'var(--color-bg)' }}>
      <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden">
        <div className="absolute w-[700px] h-[700px] rounded-full top-[-260px] right-[-200px] animate-float"
          style={{ background: 'radial-gradient(circle, rgba(46,54,66,0.08), transparent 65%)' }} />
        <div className="absolute w-[600px] h-[600px] rounded-full bottom-[-200px] left-[-160px] animate-float animation-delay-2000"
          style={{ background: 'radial-gradient(circle, rgba(139,92,246,0.05), transparent 65%)' }} />
      </div>

      <Topbar />

      <div className="relative z-10 pt-[52px] p-5 flex items-center justify-center min-h-[calc(100vh-52px)]">
        <div className="w-full max-w-[500px]">
          <div className="text-center mb-8 animate-slide-up">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-3"
              style={{ background: 'var(--color-orangeBg)', border: '1px solid var(--color-orangeBorder)' }}>
              <i className="fas fa-lock text-[9px]" style={{ color: 'var(--color-text)' }} />
              <span className="text-[9px] font-mono tracking-wide" style={{ color: 'var(--color-text)' }}>SECURITY</span>
            </div>
            <h1 className="font-syne text-3xl md:text-4xl font-extrabold mb-2" style={{ color: 'var(--color-text)' }}>
              Change <span style={{ color: 'var(--color-text)' }}>Password</span>
            </h1>
            <p className="text-sm" style={{ color: 'var(--color-textSecondary)' }}>Update your account password to keep your funds secure</p>
          </div>

          <div className="rounded-2xl overflow-hidden animate-slide-up animation-delay-100" style={cardStyle}>
            <div className="flex items-center gap-3 px-6 py-4 border-b" style={{ background: 'var(--color-bgCard)', borderColor: 'var(--color-borderMedium)' }}>
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'var(--color-orangeBg)', border: '1px solid var(--color-orangeBorder)', color: 'var(--color-text)' }}>
                <i className="fas fa-key text-base" />
              </div>
              <div>
                <div className="font-syne text-base font-extrabold" style={{ color: 'var(--color-text)' }}>Password Update</div>
                <div className="text-[9px] font-mono" style={{ color: 'var(--color-textDim)' }}>Enter your credentials</div>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-5">
              {[
                { label: 'Current Password', value: currentPassword, setter: setCurrentPassword, show: showCurrent, setShow: setShowCurrent, icon: 'fa-lock' },
              ].map(field => (
                <div key={field.label}>
                  <label className="text-[9px] font-mono tracking-wide uppercase block mb-2 flex items-center gap-1" style={{ color: 'var(--color-text)' }}>
                    <i className={`fas ${field.icon} text-[8px]`} /> {field.label}
                  </label>
                  <div className="relative">
                    <input type={field.show ? 'text' : 'password'} value={field.value}
                      onChange={(e) => field.setter(e.target.value)} placeholder={`Enter your ${field.label.toLowerCase()}`}
                      className="w-full rounded-lg px-3 py-2 pr-10 font-dm text-sm" style={inputStyle}
                      onFocus={e => e.target.style.borderColor = 'var(--color-orange)'}
                      onBlur={e => e.target.style.borderColor = 'var(--color-borderMedium)'} />
                    <button type="button" onClick={() => field.setShow(!field.show)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 transition-colors"
                      style={{ color: 'var(--color-textDim)' }}
                      onMouseEnter={e => e.currentTarget.style.color = 'var(--color-text)'}
                      onMouseLeave={e => e.currentTarget.style.color = 'var(--color-textDim)'}>
                      <i className={`fas ${field.show ? 'fa-eye-slash' : 'fa-eye'} text-xs`} />
                    </button>
                  </div>
                </div>
              ))}

              <div>
                <label className="text-[9px] font-mono tracking-wide uppercase block mb-2 flex items-center gap-1" style={{ color: 'var(--color-text)' }}>
                  <i className="fas fa-key text-[8px]" /> New Password
                </label>
                <div className="relative">
                  <input type={showNew ? 'text' : 'password'} value={newPassword}
                    onChange={(e) => { setNewPassword(e.target.value); checkPasswordStrength(e.target.value); }}
                    placeholder="Enter new password"
                    className="w-full rounded-lg px-3 py-2 pr-10 font-dm text-sm" style={inputStyle}
                    onFocus={e => e.target.style.borderColor = 'var(--color-orange)'}
                    onBlur={e => e.target.style.borderColor = 'var(--color-borderMedium)'} />
                  <button type="button" onClick={() => setShowNew(!showNew)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 transition-colors"
                    style={{ color: 'var(--color-textDim)' }}
                    onMouseEnter={e => e.currentTarget.style.color = 'var(--color-text)'}
                    onMouseLeave={e => e.currentTarget.style.color = 'var(--color-textDim)'}>
                    <i className={`fas ${showNew ? 'fa-eye-slash' : 'fa-eye'} text-xs`} />
                  </button>
                </div>
                {newPassword && (
                  <div className="mt-2">
                    <div className="flex gap-1 mb-1">
                      {[1, 2, 3, 4].map((i) => (
                        <div key={i} className="flex-1 h-1 rounded-full transition-all"
                          style={{ background: i <= passwordStrength ? strengthColors[passwordStrength - 1] : 'var(--color-borderMedium)' }} />
                      ))}
                    </div>
                    <div className="text-[8px] flex justify-between" style={{ color: 'var(--color-textSecondary)' }}>
                      <span>Password strength:</span>
                      <span style={{ color: strengthColors[passwordStrength - 1] || 'var(--color-textSecondary)' }}>{strengthLabels[passwordStrength - 1] || 'Enter a password'}</span>
                    </div>
                  </div>
                )}
              </div>

              <div>
                <label className="text-[9px] font-mono tracking-wide uppercase block mb-2 flex items-center gap-1" style={{ color: 'var(--color-text)' }}>
                  <i className="fas fa-check-circle text-[8px]" /> Confirm New Password
                </label>
                <div className="relative">
                  <input type={showConfirm ? 'text' : 'password'} value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)} placeholder="Re-enter new password"
                    className="w-full rounded-lg px-3 py-2 pr-10 font-dm text-sm" style={inputStyle}
                    onFocus={e => e.target.style.borderColor = 'var(--color-orange)'}
                    onBlur={e => e.target.style.borderColor = 'var(--color-borderMedium)'} />
                  <button type="button" onClick={() => setShowConfirm(!showConfirm)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 transition-colors"
                    style={{ color: 'var(--color-textDim)' }}
                    onMouseEnter={e => e.currentTarget.style.color = 'var(--color-text)'}
                    onMouseLeave={e => e.currentTarget.style.color = 'var(--color-textDim)'}>
                    <i className={`fas ${showConfirm ? 'fa-eye-slash' : 'fa-eye'} text-xs`} />
                  </button>
                </div>
                {confirmPassword && newPassword !== confirmPassword && (
                  <div className="text-[8px] font-mono mt-1" style={{ color: 'var(--color-red)' }}>Passwords do not match</div>
                )}
              </div>

              <button type="submit" disabled={isLoading}
                className="w-full flex items-center justify-center gap-2 py-3 rounded-full font-syne text-sm font-bold transition-all active:translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ background: 'var(--color-gradientOrange)', color: 'var(--color-textInverse)' }}>
                {isLoading ? <><span className="inline-block w-3 h-3 border-2 rounded-full animate-spin mr-1" style={{ borderColor: 'var(--color-textInverse)', borderTopColor: 'transparent' }} /> Updating</>
                  : <><i className="fas fa-save mr-1" /> Update Password</>}
              </button>
            </form>
          </div>
        </div>
      </div>

      <Toast />
      <style>{`
        @keyframes float { 0%, 100% { transform: translate(0, 0); } 50% { transform: translate(28px, -28px); } }
        @keyframes slide-up { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        .animate-float { animation: float 20s ease-in-out infinite; }
        .animation-delay-2000 { animation-delay: -8s; }
        .animate-slide-up { animation: slide-up 0.6s ease-out both; }
        .animation-delay-100 { animation-delay: 0.1s; }
      `}</style>
    </div>
  );
};

export default ChangePassword;